import { h, Component } from 'preact';
import { accessNested, joinClasses, sortObjectByOrder } from 'utils/utils';
import { smoothScrollTo } from 'utils/smoothScroll';
import { Easing } from 'utils/math';
import s from 'base/boltToolkit/boltToolkitApp.sss';
import HTML from 'components/core/html/html';
import CampaignGenerator from 'base/boltToolkit/campaignGenerator';
import ToolkitMenu from 'base/boltToolkit/toolkitMenu';
import Link from 'components/bolt/link/link';
import PowsterLogo from 'components/bolt/icons/powsterLogo';

export default class BoltToolkitApp extends Component {
	constructor(props) {
		super(props);

		this.state = {
			activeSection: 'intro'
		};

		this.pages = this.getPages();

		this.scrollTo = this.scrollTo.bind(this);
		this.handleScroll = this.handleScroll.bind(this);
		this.updateActiveSection = this.updateActiveSection.bind(this);
		this.downloadImage = this.downloadImage.bind(this);
	}

	componentDidMount() {
		window.addEventListener('scroll', this.handleScroll);
		window.addEventListener('wheel', this.handleScroll);
		window.addEventListener('touchmove', this.handleScroll);
		window.scrollToContact = () => this.scrollTo('contact');
		if (window.location.hash) {
			this.scrollTo(window.location.hash.replace('#', ''));
			this.updateActiveSection(window.location.hash.replace('#', ''));
		}
	}

	componentWillUnmount() {
		window.removeEventListener('scroll', this.handleScroll);
		window.removeEventListener('wheel', this.handleScroll);
		window.removeEventListener('touchmove', this.handleScroll);
	}

	download(image, link) {
		const isEdge = accessNested(this.props, 'client.isEdge', false);
		const isIOS = accessNested(this.props, 'client.isIOS', false);

		if (isIOS) {
			link.target = '_blank';
		} else if (!isEdge) {
			const c = document.createElement('canvas');
			const context = c.getContext('2d');
			c.width = image.naturalWidth;
			c.height = image.naturalHeight;
			context.drawImage(image, 0, 0);
			link.href = c.toDataURL('image/jpeg');
			link.style.display = 'none';
		}

		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	downloadImage() {
		const src = this.shareImage.src;
		let link = document.createElement('a');
		link.download = 'share-image.jpg';
		link.href = src;

		this.download(this.shareImage, link);
	}

	scrollTo(section) {
		this.updateActiveSection(section);
		this.setState({ scrolling: true });
		let el = document.getElementById(section);
		if (el) {
			smoothScrollTo('root', el, 500, {
				easing: Easing.ease,
				relativeTo: 'middle',
				callback: () => this.setState({ scrolling: false })
			});
		}
	}

	updateActiveSection(activeSection) {
		this.setState({ activeSection });
		let hash = activeSection === 'intro' ? '' : `#${activeSection}`;
		window.history.replaceState('', '', `.${hash}`);
	}

	getPages() {
		const pagesData = this.props.initialStore?.pages || {};
		const pageIdToNiceName = id => id.replace(/^bolt([A-Z])/, (m, p1) => p1.toLowerCase()).replace(/^([a-z])|([a-z])([A-Z])/g, (m, p1, p2, p3) => p1?.toUpperCase() || `${p2} ${p3}`);
		return sortObjectByOrder(pagesData, (page, id) => {
			const base = page?.base || 'bolt';
			if (!page?.path || base !== 'bolt') {
				return;
			}
			return {
				path: page.path,
				name: page.name || pageIdToNiceName(id),
			};
		}).filter(e => e);
	}

	handleScroll() {
		if (this.state.scrolling) return;

		const watchedSections = [...this.base.querySelectorAll('section[id]')].map(section => ({
			id: section.id,
			offset: section.offsetTop - (window.innerHeight / 2),
		}));
		const activeSection = watchedSections.reverse().find(({ offset }) => offset <= document.body.scrollTop)?.id || 'intro';

		if (this.state.activeSection !== activeSection) {
			this.updateActiveSection(activeSection);
		}
	}

	renderIntro(data) {
		const movieTitle = accessNested(this.props, 'initialStore.appTitle', '');
		const href = accessNested(this.props, 'initialStore.appUrl');
		const { pageTitle, tagline } = data;

		return (
			<section class={joinClasses(s.intro, s.section)}>
				<Link href={href} target="_blank">
					<h1 class={s.h1}>{movieTitle}</h1>
				</Link>
				<h2 class={s.h3}>{pageTitle}</h2>
				<p class={s.p}>{tagline}</p>
			</section>
		);

	}

	renderShare(data) {
		const shareImage = accessNested(this.props, 'initialStore.shareImage', '');
		const { shareTitle, description, download } = data;

		return (
			<section class={joinClasses(s.section, s.share)}>
				<h1 class={s.h2}>{shareTitle}</h1>
				<img
					ref={ref => this.shareImage = ref}
					onClick={this.downloadImage}
					class={s.shareImage}
					src={shareImage}
					crossOrigin="anonymous"
					alt=""
				/>
				<a class={joinClasses(s.download, s.p, s.bold)} onClick={this.downloadImage}>
					<div class={s.icon} />
					<span>{download}</span>
				</a>
				<HTML class={s.p} content={description} markdown />
			</section>
		);
	}

	renderCampaign(data) {
		const { title, subtitle, steps = {}, note, generator } = data;
		const appUrl = accessNested(this.props, 'initialStore.appUrl', '');
		const copy = accessNested(this.props, 'initialStore.copy', {});

		return (
			<section id="campaign" class={joinClasses(s.section, s.campaign)}>
				<h1 class={joinClasses(s.title, s.p)}>{title}</h1>
				<h2 class={s.h2}>{subtitle}</h2>
				<ul class={s.steps}>
					{Object.entries(steps).map(([step, blurb]) => (
						<li class={s.step}>
							<h3 class={s.h3}>{step}</h3>
							<HTML class={s.p} markdown content={blurb} />
						</li>
					))}
				</ul>
				<p class={joinClasses(s.p, s.note)}>{note}</p>
				<CampaignGenerator pages={this.pages} data={generator} appUrl={appUrl} copy={copy} />
			</section>
		);
	}

	renderSEO(data) {
		const { title, subtitle, description, tips } = data;
		return (
			<section id="seo" class={joinClasses(s.section, s.seo)}>
				<h1 class={joinClasses(s.title, s.p)}>{title}</h1>
				<h2 class={s.h2}>{subtitle}</h2>
				<p class={joinClasses(s.p, s.bold, s.description)}>{description}</p>
				<ul class={s.tips}>
					{sortObjectByOrder(tips).map(tip => {
						const { id, tipTitle, tipSections, tipCTA = {} } = tip;
						const { label, href } = tipCTA;
						return (
							<li class={joinClasses(s.tip, s[id])}>
								{!!tipTitle && <h3 class={s.h2}>{tipTitle}</h3>}
								<ul class={s.tipSections}>
									{Object.entries(tipSections).map(([type, section]) => {
										const { icon, title: tipSectionTitle, description: tipDescription } = section;
										return (
											<li class={joinClasses(s.tipSection, s[type])}>
												<div class={s.tipHeader}>
													<img class={s.tipIcon} src={icon} alt="" />
													<h4 class={s.h3}>{tipSectionTitle}</h4>
												</div>
												<HTML class={joinClasses(s.p, s.tipDescription)} markdown content={tipDescription} />
											</li>
										);
									})}
								</ul>
								{!!label && <Link class={joinClasses(s.tipCTA, s.bold)} href={href} target="_blank">{label}</Link>}
							</li>
						);
					})}
				</ul>
			</section>
		);
	}

	renderAnalytics(data) {
		const { title, sectionIcon, subtitle, description, dashboard, note, pixel } = data;
		const GADashboard = accessNested(this.props, 'initialStore.toolkitData.GADashboard', '');
		return (
			<section id="analytics" class={joinClasses(s.section, s.analytics)}>
				<h1 class={joinClasses(s.title, s.p)}>{title}</h1>
				<div class={s.sectionHeader}>
					<h2 class={s.h2}>{subtitle}</h2>
					<img class={s.sectionIcon} src={sectionIcon} alt="" />
				</div>
				<div class={s.dashboardWrapper}>
					<p class={joinClasses(s.p, s.dashboardDescription)}>{description}</p>
					<a class={joinClasses(s.p, s.bold, s.dashboard)} href={GADashboard} target="_blank">{dashboard}</a>
				</div>
				<HTML class={s.p} content={note} markdown />
				{!!pixel && this.renderPixel(pixel)}
			</section>
		);

	}

	renderScreenings(data) {
		const { sectionIcon, subtitle, description, button, title } = data;
		return (
			<section id="screenings" class={joinClasses(s.analytics, s.section)}>
				<h1 class={joinClasses(s.title, s.p)}>{title}</h1>
				<div class={s.sectionHeader}>
					<h2 class={s.h2}>{subtitle}</h2>
					<img class={s.sectionIcon} src={sectionIcon} alt="" />
				</div>
				<div class={s.dashboardWrapper}>
					<p class={joinClasses(s.p, s.dashboardDescription)}>{description}</p>
					<a
						class={joinClasses(s.p, s.bold, s.dashboard, s.child)}
						href={accessNested(this.props, 'initialStore.toolkitData.ScreeningsDashboard') || ''}
						target="_blank"
					>
						{button}
					</a>
				</div>
			</section>
		);
	}

	renderPixel(data) {
		const { sectionIcon, subtitle, description, link, button } = data;
		return [
			<div class={s.sectionHeader}>
				<h2 class={s.h2}>{subtitle}</h2>
				<img class={s.sectionIcon} src={sectionIcon} alt="" />
			</div>,
			<div class={s.dashboardWrapper}>
				<p class={joinClasses(s.p, s.dashboardDescription)}>{description}</p>
				<a
					class={joinClasses(s.p, s.bold, s.dashboard, s.child)}
					href={link}
					target="_blank"
				>
					{button}
				</a>
			</div>
		];
	}

	renderContact(data) {
		const { title, subtitle, description, offices = {} } = data;
		return (
			<section id="contact" class={joinClasses(s.section, s.contact)}>
				<h1 class={joinClasses(s.title, s.p)}>{title}</h1>
				<h2 class={s.h2}>{subtitle}</h2>
				<p class={joinClasses(s.p, s.bold, s.description)}>{description}</p>
				<ul class={s.offices}>
					{Object.values(offices).map(office => {
						const { name, email, number } = office;
						return (
							<li class={s.office}>
								<h3 class={s.h3}>{name}</h3>
								<a class={joinClasses(s.p, s.email)} href={'mailto:' + email}>{email}</a>
								<p class={joinClasses(s.p, s.number)}>{number}</p>
							</li>
						);
					})}
				</ul>
			</section>
		);
	}

	renderLearnMore(data) {
		const { title, subtitle, items } = data;
		return (
			<section id="learnMore" class={joinClasses(s.section, s.learnMore)}>
				<h1 class={joinClasses(s.title, s.p)}>{title}</h1>
				<h2 class={s.h2}>{subtitle}</h2>
				<ul class={s.items}>
					{sortObjectByOrder(items).map(item => {
						const { icon, title, link } = item;
						return (
							<li>
								<Link class={s.item} href={link} target="_blank">
									<img class={s.itemIcon} src={icon} alt="" />
									<h3 class={s.h3}>{title}</h3>
								</Link>
							</li>
						);
					})}
				</ul>
			</section>
		);
	}

	renderFooter() {
		const footer = accessNested(this.props, 'initialStore.toolkitData.footer', {});
		const { links, copyright } = footer;

		return (
			<footer class={s.footer}>
				<div class={s.links}>
					{sortObjectByOrder(links).map(item => {
						return <Link class={s.link} href={item.link} target="_blank">{item.name}</Link>;
					})}
				</div>
				<div class={s.powster}>
					<div class={s.copyright}>{copyright}</div>
					<Link href="https://www.powster.com" target="_blank">
						<PowsterLogo class={s.logo} />
					</Link>
				</div>
			</footer>
		);
	}

	render(props, state) {
		const data = props.initialStore?.toolkitData || {};
		const sections = data.sections || {};
		const checklist = data.checklist || {};
		const footer = data.footer || {};
		const {
			intro,
			share,
			campaign,
			seo,
			analytics,
			contact,
			learnMore,
			screenings,
		} = sections;

		return (
			<main class={s.wrapper}>
				<ToolkitMenu
					id={props.initialStore?.id}
					sections={sections}
					checklist={checklist}
					activeSection={state.activeSection}
					scrollTo={this.scrollTo}
				/>
				<div class={s.content}>
					{intro && this.renderIntro(intro)}
					{share && this.renderShare(share)}
					{campaign && this.renderCampaign(campaign)}
					{seo && this.renderSEO(seo)}
					{analytics && this.renderAnalytics(analytics)}
					{screenings && this.renderScreenings(screenings)}
					{contact && this.renderContact(contact)}
					{learnMore && this.renderLearnMore(learnMore)}
					{footer && this.renderFooter(footer)}
				</div>
			</main>
		);
	}
}
